import { ContentCard } from "@msuite/ui/content-card";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { DataTable } from "@msuite/ui/table";
import { useMemo } from "react";
import { canManageMembers, roleDisplayName } from "@/components/citadel";
import { useCitadel } from "@/hooks/use-citadel";
import { useRoles } from "@/hooks/use-roles";
import { useUserPermissionsForm } from "@/hooks/use-user-permissions-form";
import type { UserPanelBaseProps } from "../../types";
import { createColumns, type UserRoleRow } from "./table-config";

/**
 * The user's roles as a table over the whole catalogue — the roles they carry and the ones
 * they do not, told apart by a status tag, with activate/deactivate as row actions for callers
 * who may write roles. A table, because it matches the role page's members view, brings
 * search once the catalogue grows, and has room for the status column.
 *
 * The member system role reads as always active and offers no action: every non-owner carries
 * it by standing, and the server refuses its membership writes.
 */
export function UserRoles({ userId }: UserPanelBaseProps) {
	/** Context */
	const { t, language } = useTranslation();

	/** Hooks */
	const { canManageRoles } = useCitadel();

	/** Query */
	const { form } = useUserPermissionsForm(userId);
	const { roles, isLoading } = useRoles();

	/** Computed */
	const rows = useMemo<UserRoleRow[]>(() => {
		const carriedRoleIds = new Set(form?.roles.map((role) => role.id));
		return roles.map((role) => {
			const isLocked = !canManageMembers(role);
			return {
				id: role.id,
				name: roleDisplayName(role),
				isSystem: role.isSystem,
				// The member row is active whatever the data says: standing membership is the
				// invariant, and a backfill gap must not render the base role as absent.
				isActive: isLocked || carriedRoleIds.has(role.id),
				isLocked,
			};
		});
	}, [roles, form]);
	// `language` is a dependency although the callback does not read it: the column headers are
	// rendered through `localized(...)` at build time, so a language switch has to rebuild them.
	const columns = useMemo(
		() => createColumns({ userId, withActions: canManageRoles }),
		[userId, canManageRoles, language],
	);

	/** Guard */
	if (!form) return null;

	/** Render */
	if (form.isOwner) {
		return (
			<ContentCard
				title={t("roles.roles")}
				icon={icon("Lock")}
			>
				{/* Owners carry no roles at all — the resolver answers for them before it ever
				    reads one, so a table of toggles here would be a lie with a working control. */}
				<p className="label">{t("permissions.owner_role_note")}</p>
			</ContentCard>
		);
	}

	return (
		<DataTable
			data={rows}
			columns={columns}
			persistenceKey="user-roles-table"
			isLoading={isLoading}
			// The rows derive from two queries this page already holds; every write invalidates
			// those rather than refetching the table.
			refetch={undefined}
			queryKey={undefined}
			menu={null}
		/>
	);
}
